import RestDataService from '@yakit/core/api/RestDataService'
import { problemHandler } from '@yakit/ui/svelte'
import type { Wallet } from '~/ipay/types'  
import type { RestDataApi } from '@yakit/core/api/dataApi'

/**
 * Wallet Rest Api additions
 */
export default function (opts) {

  const detApi: RestDataApi = RestDataService({ ...opts, key: 'ipay/wallet' })

  detApi.credentials = async (queryParams) => {
    const response = await detApi.post({ path: 'credentials' })
    // await fetch(`http://localhost:8080/api/ipay/wallet/credentials`, {
    //   method: 'POST',
    // })
    console.log('response', response)
  
    return response
  }

  /**
   * Generate Fiserv security credentials
   * 
   * TEMPORARY POC IMPLEMENTATION - Returns hardcoded test credentials
   * TODO: Replace with real backend endpoint for production
   * 
   * @param {Object} params
   */
  detApi.generateKey = async (queryParams) => {
    // Simulate API delay
    const response = await fetch(`http://localhost:3010/api/fiserv/keys/generate`)
    const data = await response.json()
    console.log('generateKey', data)
    return data

    
  }

  /**
   * Create payment token from Fiserv payment session via backend gateway.
   *
   * @param {string} sessionId - Payment session ID from security credentials
   * @returns {Promise} Token response including tokenId and card details
   */
  detApi.token = async (sessionId) => {
    try {
      console.log('[WalletApi] Creating token from session:', sessionId)
      const result = await detApi.post({ path: 'token', json: { sessionId } })
      console.log('[WalletApi] Token created:', result)
      return result
    } catch (error) {
      console.error('[WalletApi] Token creation error:', error)
      problemHandler.handleError(error)
      throw error
    }
  }

  /**
   * Tokenize Fiserv card data to create wallet token.
   * Creates a payment token from a Fiserv hosted-fields payment session.
   *
   * @param {Object} data - Contains sessionId and optional card holder info
   * @param {string} data.sessionId - Payment session ID from security credentials
   * @returns {Promise} Token response with tokenData, card details, etc.
   */
  detApi.tokenizeCard = async (data): Promise<Partial<Wallet>> => {
    try {
      console.info('[WalletApi] Tokenizing card with data:', data)

      if (!data.sessionId) {
        throw new Error('sessionId is required for tokenization')
      }

      const result = await detApi.post({ path: 'token', json: { sessionId: data.sessionId } })
      console.info('[WalletApi] Token created:', result)

      if (!result?.ok) {
        throw new Error(result?.detail || result?.title || 'Tokenization failed')
      }

      const proc = result.processorResponse || {}
      return {
        token: result.token,
        last4: proc.last4,
        ccExpireMonth: proc.expirationMonth,
        ccExpireYear: proc.expirationYear,
        payType: 'Card',
      }
    } catch (error) {
      console.error('[WalletApi] Tokenization error:', error)
      problemHandler.handleError(error)
      throw error
    }
  }

  /**
   * Process a payment charge via Fiserv
   * 
   * If walletId is provided, fetches wallet data from Grails backend first.
   * Otherwise uses token and card expiration from the request directly.
   * 
   * @param {Object} params - Charge parameters
   * @param {string} params.walletId - Optional wallet ID to fetch from backend
   * @param {string} params.token - Payment token (required if no walletId)
   * @param {number} params.amount - Charge amount (required)
   * @param {string} params.ccExpireMonth - Card expiration month (required if no walletId)
   * @param {string} params.ccExpireYear - Card expiration year (required if no walletId)
   * @param {boolean} params.captureFlag - Whether to capture immediately (default: true)
   * @param {string} params.merchantOrderId - Optional merchant order ID
   * @returns {Promise} Charge response from Fiserv
   * 
   * @example
   * // Using walletId
   * const result = await walletApi.charge({
   *   walletId: '123e4567-e89b-12d3-a456-426614174000',
   *   amount: 10.50,
   *   merchantOrderId: 'ORDER-12345'
   * })
   * 
   * @example
   * // Using direct token
   * const result = await walletApi.charge({
   *   token: '1234567890123456',
   *   amount: 10.50,
   *   ccExpireMonth: '12',
   *   ccExpireYear: '2025',
   *   merchantOrderId: 'ORDER-12345'
   * })
   */
  detApi.charge = async (params) => {
    console.log('charge', params)
    try {
      console.log('[WalletApi] Processing charge with params:', params)

      let token = params.token
      let ccExpireMonth = params.ccExpireMonth
      let ccExpireYear = params.ccExpireYear

      // If walletId is provided, fetch wallet data from Grails backend
      if (params.walletId) {
        console.log('[WalletApi] Fetching wallet data for ID:', params.walletId)
        
        const wallet = await detApi.get(params.walletId)
        console.log('[WalletApi] Wallet data retrieved:', wallet)

        if (!wallet) {
          throw new Error(`Wallet not found: ${params.walletId}`)
        }

        // Extract payment data from wallet
        token = wallet.token
        ccExpireMonth = wallet.ccExpireMonth
        ccExpireYear = wallet.ccExpireYear

        if (!token) {
          throw new Error(`Wallet ${params.walletId} does not have a payment token`)
        }

        if (!ccExpireMonth || !ccExpireYear) {
          throw new Error(`Wallet ${params.walletId} is missing card expiration data`)
        }
      }

      // Validate required fields
      if (!token) {
        throw new Error('Payment token is required (provide token or walletId)')
      }

      if (!params.amount || params.amount <= 0) {
        throw new Error('Valid charge amount is required')
      }

      if (!ccExpireMonth || !ccExpireYear) {
        throw new Error('Card expiration date is required (provide ccExpireMonth/ccExpireYear or walletId)')
      }

      // Build charge request
      const chargeData = {
        token,
        amount: params.amount,
        ccExpireMonth: ccExpireMonth.toString().padStart(2, '0'),
        ccExpireYear: ccExpireYear.toString(),
        captureFlag: params.captureFlag !== undefined ? params.captureFlag : true,
        merchantOrderId: params.merchantOrderId || `CHARGE-${Date.now()}`
      }

      console.log('[WalletApi] Calling Fiserv charge endpoint with:', chargeData)

      // Call Fiserv proxy server charge endpoint
      const response = await fetch('http://localhost:3010/api/fiserv/charges', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chargeData),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `Charge failed with status ${response.status}`)
      }

      const result = await response.json()
      console.log('[WalletApi] Charge result:', result)

      // Check approval status
      const approvalStatus = result.paymentReceipt?.processorResponseDetails?.approvalStatus
      if (approvalStatus !== 'APPROVED') {
        console.warn('[WalletApi] Charge was not approved:', approvalStatus)
      }

      return result
    } catch (error) {
      console.error('[WalletApi] Charge error:', error)
      problemHandler.handleError(error)
      throw error
    }
  }

  return detApi
}
